'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import service from '../../../service';
import { cn } from '../../../utils';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { CircleAlert } from 'lucide-react';

const DashboardSchema = z.object({
  name: z
    .string()
    .min(2, 'Please enter at least 2 characters')
    .max(50, 'Keep it under 50 characters'),
  description: z
    .string()
    .min(2, 'Description is required')
    .max(200, 'Keep it under 200 characters'),
  embeddableResource: z
    .string()
    .min(2, 'Omni ID is required')
    .max(20, 'Keep it under 20 characters'),
});

export type DashboardFormValues = z.infer<typeof DashboardSchema>;

type Props = {
  className?: string;
  defaultValues?: Partial<DashboardFormValues>;
  submitLabel?: string;
  /** Invalidate this query key after success */
  invalidateKey?: (string | number)[];
  onSuccess?: (created?: unknown) => void;

  /** NEW: form id so parent buttons can submit with form={formId} */
  formId?: string;
  /** NEW: hide internal Reset/Submit buttons (parent supplies them) */
  hideInternalActions?: boolean;
  /** When provided, switches to edit mode and calls updateDashboard */
  editId?: string;
};

export default function DashboardForm({
  className,
  defaultValues,
  submitLabel,
  invalidateKey = ['dashboards'],
  onSuccess,

  formId = 'dashboard-form',
  hideInternalActions = false,
  editId,
}: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!editId;
  const resolvedSubmitLabel =
    submitLabel ?? (isEditMode ? 'Update dashboard' : 'Create dashboard');

  const form = useForm<DashboardFormValues>({
    resolver: zodResolver(DashboardSchema),
    defaultValues: {
      name: '',
      description: '',
      embeddableResource: '',
      ...defaultValues,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isSubmitting },
  } = form;

  const mutation = useMutation({
    mutationFn: async (values: DashboardFormValues) => {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        embeddableResource: values.embeddableResource?.trim() || undefined,
      };
      if (isEditMode) {
        return service.updateDashboard(editId, payload);
      }
      return service.createDashboard(payload);
    },
    onSuccess: async (result) => {
      if (invalidateKey?.length) {
        await queryClient.invalidateQueries({ queryKey: invalidateKey });
      }
      onSuccess?.(result);
      toastSuccess(isEditMode ? 'dashboard updated' : 'dashboard created');
      if (!isEditMode) {
        reset({ name: '', description: '', embeddableResource: '' });
      }
    },
    onError: (err: any) => {
      setError('root', {
        type: 'server',
        message:
          err?.message ||
          `Something went wrong while ${isEditMode ? 'updating' : 'creating'} the dashboard. Please try again.`,
      });
    },
  });

  const onSubmit = (values: DashboardFormValues) => {
    mutation.mutate(values);
  };

  const pending = isSubmitting || mutation.isPending;

  return (
    <div className={cn('max-w-xl', className)}>
      <Form {...form}>
        {/* IMPORTANT: expose id for external submit via <Button form={formId}/> */}
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., General" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input placeholder="Description..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="embeddableResource"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Embeddable Resource</FormLabel>
                <FormControl>
                  <Input placeholder="Resource..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* top-level error */}
          {form.formState.errors.root?.message ? (
            <Alert variant="destructive" className="my-4">
              <CircleAlert />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root?.message ??
                  'Error creating dashboard. Please try again later.'}
              </AlertDescription>
            </Alert>
          ) : null}

          {!hideInternalActions && (
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => reset()}
                disabled={pending}
              >
                Reset
              </Button>
              <Button type="submit" variant="primary" loading={pending}>
                {resolvedSubmitLabel}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
